import React, { useEffect, useMemo, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { userRequest } from "../../utils/requestMethods";

const AssignSubjectsModal = ({ onClose, teacher, school, onSuccess }) => {
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [classId, setClassId] = useState("");
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [roleLevel, setRoleLevel] = useState("");
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [subjectSearch, setSubjectSearch] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchClasses = async () => {
      if (!school?.code) return;

      try {
        setLoadingClasses(true);
        const { data } = await userRequest.get(`classes?code=${school?.code}`);
        if (isMounted) setClasses(data.classes || []);
      } catch (error) {
        if (isMounted) console.error("Error fetching classes:", error);
      } finally {
        if (isMounted) setLoadingClasses(false);
      }
    };

    fetchClasses();

    return () => {
      isMounted = false;
    };
  }, [school?.code]);

  // Fetch role levels
  useEffect(() => {
    let isMounted = true;
    const fetchRoles = async () => {
      try {
        setLoadingRoles(true);
        const { data } = await userRequest.get(`/teachers/roles-levels`);
        if (isMounted) setAvailableRoles(data.roleLevels || []);
      } catch (err) {
        if (isMounted) console.error("Error fetching role levels:", err);
      } finally {
        if (isMounted) setLoadingRoles(false);
      }
    };
    fetchRoles();
    return () => { isMounted = false; };
  }, []);

  // Fetch all permissions
  useEffect(() => {
    let isMounted = true;
    const fetchPerms = async () => {
      try {
        setLoadingPermissions(true);
        const { data } = await userRequest.get(`/teachers/permissions`);
        if (isMounted) setAvailablePermissions(data.permissions || []);
      } catch (err) {
        if (isMounted) console.error("Error fetching permissions:", err);
      } finally {
        if (isMounted) setLoadingPermissions(false);
      }
    };
    fetchPerms();
    return () => { isMounted = false; };
  }, []);

  // Pre-check default permissions when a role is selected
  useEffect(() => {
    let isMounted = true;
    const fetchDefaultPerms = async () => {
      if (!roleLevel) return;
      try {
        const { data } = await userRequest.get(`/teachers/default-permissions`, {
          params: { role: roleLevel, assignmentType: 'subject' }
        });
        const defaults = (data.permissions || []).map(p => p.value);
        if (isMounted) setSelectedPermissions(defaults);
      } catch (err) {
        if (isMounted) console.error("Error fetching default permissions:", err);
      }
    };
    fetchDefaultPerms();
    return () => { isMounted = false; };
  }, [roleLevel]);

  const permissionsByCategory = useMemo(() => {
    const map = {};
    for (const perm of availablePermissions) {
      const cat = perm.category || 'Other';
      if (!map[cat]) map[cat] = [];
      map[cat].push(perm);
    }
    return map;
  }, [availablePermissions]);

  const togglePermission = (value) => {
    setSelectedPermissions(prev => prev.includes(value)
      ? prev.filter(v => v !== value)
      : [...prev, value]
    );
  };

  const fetchSubjects = async (classId) => {
    if (!classId) return;
    setLoadingClasses(true);
    try {
      const { data } = await userRequest.get(`/subjects/${school?._id}/${classId}?limit=100`);
      setSubjects(data.subjects || []);
      // Reset selected subjects when class changes
      setSelectedSubjects([]);
      setSubjectSearch("");
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setSubjects([]);
    } finally {
      setLoadingClasses(false);
    }
  };

  useEffect(() => {
    fetchSubjects(classId);
  }, [classId, school?._id]);

  const toggleSubject = (subjectId) => {
    setSelectedSubjects(prev => 
      prev.includes(subjectId)
        ? prev.filter(id => id !== subjectId)
        : [...prev, subjectId]
    );
  };

  const filteredSubjects = useMemo(() => {
    if (!subjectSearch.trim()) return subjects;
    const search = subjectSearch.toLowerCase();
    return subjects.filter(subj => 
      subj.subject_name?.toLowerCase().includes(search)
    );
  }, [subjects, subjectSearch]);

  const selectAllSubjects = () => {
    const allIds = filteredSubjects.map(s => s._id);
    setSelectedSubjects(prev => {
      const combined = [...new Set([...prev, ...allIds])];
      return combined;
    });
  };

  const deselectAllSubjects = () => {
    const filteredIds = filteredSubjects.map(s => s._id);
    setSelectedSubjects(prev => prev.filter(id => !filteredIds.includes(id)));
  };

  const allFilteredSelected = filteredSubjects.length > 0 && 
    filteredSubjects.every(subj => selectedSubjects.includes(subj._id));

  const submitHandler = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!classId) {
        setError("Please select a class.");
        return;
    }

    if (!roleLevel) {
      setError("Please select a role.");
      return;
    }

    if (!selectedSubjects.length) {
      setError("Please select at least one subject.");
      return;
    }

    try {
      setLoading(true);

      await userRequest.post(`/teachers/assign-teacher-to-subject`, {
        schoolId: school._id,
        teacherId: teacher._id,
        classId,
        subjectIds: selectedSubjects,
        roleLevel,
        permissions: selectedPermissions,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Error assigning subjects:", error);
      const payload = error?.response?.data;
      let message = "An unexpected error occurred.";
      if (typeof payload === 'string') message = payload;
      else if (payload && (payload.message || payload.error)) message = payload.message || payload.error;
      setError(message);
    } finally {
      setLoading(false);
    }
};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full md:w-1/3 lg:w-2/5 max-w-2xl shadow-lg flex flex-col max-h-[90vh]">
        {/* Header - Fixed */}
        <div className="flex justify-between items-center p-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold">Assign Subject</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes />
          </button>
        </div>
        
        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-600">{success}</p>
            </div>
          )}
          
          <div className="mb-6">
            <label className="block text-sm text-gray-600 mb-2">Select Class</label>
            <select
              onChange={(e) => setClassId(e.target.value)}
              value={classId}
              className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              disabled={loading}
            >
              <option value="" disabled>
                {loadingClasses ? "Loading classes..." : "Select a Class"}
              </option>
              {classes.length > 0 ? (
                classes.map((classe) => (
                  <option key={classe._id} value={classe._id}>
                    {classe.class_name}
                  </option>
                ))
              ) : (
                <option disabled>No classes available</option>
              )}
            </select>
          </div>
          {/* Subjects Selection */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm text-gray-600">
                Select Subjects {selectedSubjects.length > 0 && (
                  <span className="text-xs text-gray-500 font-normal">
                    ({selectedSubjects.length} selected)
                  </span>
                )}
              </label>
              {filteredSubjects.length > 0 && (
                <button
                  type="button"
                  className="text-xs text-indigo-600 hover:underline disabled:opacity-60"
                  onClick={allFilteredSelected ? deselectAllSubjects : selectAllSubjects}
                  disabled={loading || !classId}
                >
                  {allFilteredSelected ? "Deselect All" : "Select All"}
                </button>
              )}
            </div>
            {!classId ? (
              <div className="text-xs text-gray-500 p-4 border border-gray-200 rounded-lg bg-gray-50">
                Please select a class first to view available subjects.
              </div>
            ) : loadingClasses ? (
              <div className="text-xs text-gray-500 p-4 border border-gray-200 rounded-lg bg-gray-50">
                Loading subjects...
              </div>
            ) : subjects.length === 0 ? (
              <div className="text-xs text-gray-500 p-4 border border-gray-200 rounded-lg bg-gray-50">
                No subjects available for this class.
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Search input */}
                {subjects.length > 5 && (
                  <div className="p-2 border-b border-gray-200 bg-gray-50">
                    <input
                      type="text"
                      placeholder="Search subjects..."
                      value={subjectSearch}
                      onChange={(e) => setSubjectSearch(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      disabled={loading}
                    />
                  </div>
                )}
                {/* Subjects list */}
                <div className="max-h-64 overflow-y-auto p-3">
                  {filteredSubjects.length > 0 ? (
                    <div className="grid grid-cols-1 gap-2">
                      {filteredSubjects.map((subject) => (
                        <label
                          key={subject._id}
                          className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${
                            selectedSubjects.includes(subject._id)
                              ? 'bg-indigo-50 border border-indigo-200'
                              : 'hover:bg-gray-50 border border-transparent'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            checked={selectedSubjects.includes(subject._id)}
                            onChange={() => toggleSubject(subject._id)}
                            disabled={loading}
                          />
                          <span className="text-sm text-gray-700 flex-1">
                            {subject.subject_name}
                          </span>
                          {selectedSubjects.includes(subject._id) && (
                            <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 text-center py-4">
                      {subjectSearch ? "No subjects found matching your search." : "No subjects available."}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Role */}
          <div className="mb-6">
            <label className="block text-sm text-gray-600 mb-2">Select Role</label>
            {loadingRoles ? (
              <div className="text-xs text-gray-500 p-4 border border-gray-200 rounded-lg bg-gray-50">
                Loading roles...
              </div>
            ) : availableRoles.length === 0 ? (
              <div className="text-xs text-gray-500 p-4 border border-gray-200 rounded-lg bg-gray-50">
                No roles available.
              </div>
            ) : (
              <div className='flex flex-wrap gap-3'>
                {availableRoles.map(optionRole => (
                  <label
                    key={optionRole.value}
                    className={`flex-1 min-w-[200px] flex items-center justify-between border rounded-lg px-4 py-3 text-sm cursor-pointer transition-colors ${
                      roleLevel === optionRole.value
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 hover:border-indigo-400'
                    }`}
                  >
                    <div className='flex flex-col text-left flex-1'>
                      <span className='font-medium'>{optionRole.label}</span>
                      {optionRole.description && (
                        <span className='text-xs text-slate-500 mt-1'>
                          {optionRole.description}
                        </span>
                      )}
                    </div>
                    <input
                      type='radio'
                      name='teacherRole'
                      value={optionRole.value}
                      checked={roleLevel === optionRole.value}
                      onChange={(e) => setRoleLevel(e.target.value)}
                      disabled={loading}
                      className='form-radio text-indigo-600 ml-2'
                    />
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Permissions */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm text-gray-600">Permissions</label>
              <button
                type="button"
                className="text-xs text-indigo-600 hover:underline disabled:opacity-60"
                onClick={() => setSelectedPermissions(availablePermissions.map(p => p.value))}
                disabled={loadingPermissions || availablePermissions.length === 0}
              >
                Select All
              </button>
            </div>
            {loadingPermissions ? (
              <div className="text-xs text-gray-500">Loading permissions...</div>
            ) : (
              <div className="max-h-48 overflow-auto border border-gray-200 rounded p-2">
                {Object.keys(permissionsByCategory).map((cat) => (
                  <div key={cat} className="mb-3">
                    <p className="text-xs font-semibold text-gray-700 mb-1">{cat}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {permissionsByCategory[cat].map((perm) => (
                        <label key={perm.value} className="flex items-center gap-2 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            className="rounded"
                            checked={selectedPermissions.includes(perm.value)}
                            onChange={() => togglePermission(perm.value)}
                            disabled={loading}
                          />
                          <span>{perm.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                {availablePermissions.length === 0 && (
                  <div className="text-xs text-gray-500">No permissions available.</div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Footer - Fixed */}
        <div className="p-6 pt-4 border-t border-gray-100 flex-shrink-0">
          <div className="text-right">
            <button 
              onClick={submitHandler} 
              className="px-5 py-3 bg-[#3F3DA5] text-white text-sm font-semibold rounded-full hover:bg-indigo-800 transition disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={loading || !classId || selectedSubjects.length === 0}
            >
              {loading ? "Assigning..." : "Assign Now"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignSubjectsModal;
